import { useState, useEffect } from 'react';
import { getAccessToken } from '../utils/auth';
import Editor from '@monaco-editor/react';

function ModelconnectorEdit({ modelconnectorId, onCancel, onUnsavedChange }) {
  const [config, setConfig] = useState({ display_name: '', description: '', sp_id: '', params: {}, is_active: false, id: '', modified: '' });
  const [serviceProviders, setServiceProviders] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [paramsContent, setParamsContent] = useState('{}');
  const [isModified, setIsModified] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [originalConfig, setOriginalConfig] = useState({});
  const [models, setModels] = useState([]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${(date.getMonth()+1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (onUnsavedChange) {
      onUnsavedChange(isModified);
    }
  }, [isModified, onUnsavedChange]);

  useEffect(() => {
    const fetchData = async () => {
      const token = await getAccessToken();
      if (token) {
        if (modelconnectorId !== '00000000000000000000000000000000') {
          fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/modelconnector/${modelconnectorId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
            .then(response => response.json())
            .then(data => {
              setConfig(data);
              setParamsContent(JSON.stringify(data.params || {}, null, 2));
              setIsModified(false);
              setOriginalConfig({
                sp_id: data.sp_id,
                display_name: data.display_name,
                description: data.description,
                is_active: data.is_active,
                params: data.params
              });
              if (data.sp_id) {
                fetchModels(data.sp_id);
              }
            })
            .catch(error => console.error('Error fetching model connector:', error));
        }

        fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/serviceprovider?type=llm`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
          .then(response => response.json())
          .then(data => setServiceProviders(data))
          .catch(error => console.error('Error fetching service providers:', error));
      }
    };
    fetchData();
  }, [modelconnectorId]);

  const fetchModels = async (spId) => {
    if (!spId) {
      setModels([]);
      return;
    }
    const token = await getAccessToken();
    if (token) {
      fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/serviceprovider/${spId}/models`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(response => response.json())
        .then(data => setModels(data))
        .catch(error => console.error('Error fetching models:', error));
    }
  };

  const handleCancel = () => {
    if (isModified) {
      setShowCancelModal(true);
    } else {
      onCancel();
    }
  };

  const handleSave = async () => {
    const token = await getAccessToken();
    if (token) {
      const modifiedFields = {};
      if (config.sp_id !== originalConfig.sp_id) modifiedFields.sp_id = config.sp_id;
      if (config.display_name !== originalConfig.display_name) modifiedFields.display_name = config.display_name;
      if (config.description !== originalConfig.description) modifiedFields.description = config.description;
      if (config.is_active !== originalConfig.is_active) modifiedFields.is_active = config.is_active;
      
      let currentParams;
      try {
        currentParams = JSON.parse(paramsContent);
      } catch (e) {
        currentParams = {};
      }
      if (JSON.stringify(currentParams) !== JSON.stringify(originalConfig.params)) {
        modifiedFields.params = currentParams;
      }

      try {
        let response;
        if (modelconnectorId === '00000000000000000000000000000000') {
          response = await fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/modelconnector`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              sp_id: config.sp_id,
              display_name: config.display_name,
              description: config.description,
              is_active: config.is_active,
              params: currentParams
            })
          });
        } else {
          response = await fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/modelconnector/${modelconnectorId}`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(modifiedFields)
          });
        }

        if (response.ok) {
          setSaveSuccess(true);
          setSaveMessage(`${config.display_name} successfully saved!`);
          setIsModified(false);
          setOriginalConfig({
            sp_id: config.sp_id,
            display_name: config.display_name,
            description: config.description,
            is_active: config.is_active,
            params: currentParams
          });
        } else {
          const errorData = await response.json();
          setSaveSuccess(false);
          // setSaveMessage(errorData.message || 'Failed to save');
          // setSaveMessage(JSON.stringify(errorData.detail) || 'Failed to save');
          if (errorData.detail) {
            if (typeof errorData.detail === 'string') {
              setSaveMessage(errorData.detail);
            } else if (Array.isArray(errorData.detail)) {
              setSaveMessage(errorData.detail.map(d => d.loc[1]+": "+d.msg).join(', '));
            } else if (typeof errorData.detail === 'object') {
              setSaveMessage(Object.values(errorData.detail).map(d => Array.isArray(d) ? d.map(item => item.msg).join(', ') : d).join(', '));
            } else {
              setSaveMessage('Failed to save');
            }
          } else {
            setSaveMessage(errorData.message || 'Failed to save');
          }
        }
      } catch (error) {
        setSaveSuccess(false);
        setSaveMessage('Error saving model connector: ' + error.message);
      }
      setShowSaveModal(true);
    }
  };

  return (
    <div className='edit-page'>
      <div className="header-layout">
        <h2 className="header-title">Edit Model Connector</h2>
      </div>
      <div className="edit-form">
        <div className="edit-form-row-1">
          <div className="edit-form-column-1">
            <div className="form-field dropdown">
              <label className="form-label">Select an LLM Provider</label>
              <div 
                onClick={() => setShowDropdown(!showDropdown)}
                className="dropdown-trigger"
              >
                <img src={`/media/${serviceProviders.find(sp => sp.id === config.sp_id)?.display_name.toLowerCase()}-icon.png`} alt="" className="icon-small" />
                {config.sp_id ? (
                  serviceProviders.find(sp => sp.id === config.sp_id)?.display_name || 'Select an LLM provider'
                ) : 'Select an LLM provider'}
              </div>
              {showDropdown && (
                <div className="dropdown-menu">
                  {serviceProviders.map((sp) => (
                    <div 
                      key={sp.id} 
                      onClick={() => { 
                        setConfig({...config, sp_id: sp.id}); 
                        setShowDropdown(false);
                        setIsModified(true);
                        fetchModels(sp.id);
                      }}
                      className="dropdown-option"
                    >
                      <img src={`/media/${sp.display_name.toLowerCase()}-icon.png`} alt={sp.type} className="icon-small" />
                      {sp.display_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-field">
              <label className="form-label">Name</label>
              <input 
                type="text" 
                value={config.display_name} 
                onChange={(e) => {
                  setConfig({...config, display_name: e.target.value});
                  setIsModified(true);
                }}
                className="form-input"
              />
            </div>
            <div className="form-field">
              <label className="form-label">Description</label>
              <textarea 
                value={config.description} 
                onChange={(e) => {
                  setConfig({...config, description: e.target.value});
                  setIsModified(true);
                }}
                className="form-input"
                rows="5"
              />
            </div>
            <div className="form-field">
              <label className="form-label">
                <input 
                  type="checkbox" 
                  checked={config.is_active}
                  onChange={(e) => {
                    setConfig({...config, is_active: e.target.checked});
                    setIsModified(true);
                  }}
                /> Active
              </label>
            </div>
            <div className="form-field">
              <label className="form-label">Parameters</label>
              <Editor
                height="300px"
                defaultLanguage="json"
                theme="vs-dark"
                value={paramsContent}
                onChange={(value) => {
                  setParamsContent(value || '{}');
                  setIsModified(true);
                }}
                options={{ minimap: { enabled: false } }}
              />
            </div>
            <div className="form-field">
              <div className="info-box">
                <div><strong>ID:</strong> {config.id}</div>
                <div><strong>Modified:</strong> {formatDate(config.modified)}</div>
              </div>
            </div>
            <div className="button-group">
              <button onClick={handleCancel} className="btn-cancel">Cancel</button>
              <button onClick={handleSave} className="btn-save" disabled={!isModified}>Save</button>
            </div>
          </div>
          <div className="edit-form-column-12">
            {models.length > 0 && (
              <div>
                <label className="form-label">Available Models</label>
                <table className="regular-table">
                {/* <table> */}
                  <thead>
                    <tr>
                      <th data-column="generic">Model ID</th>
                      <th data-column="generic">Name</th>
                      <th data-column="generic" style={{textAlign: 'right'}}>Price In</th>
                      <th data-column="generic" style={{textAlign: 'right'}}>Price Out</th>
                      <th data-column="generic" style={{textAlign: 'right'}}>Max Token In</th>
                      <th data-column="generic" style={{textAlign: 'right'}}>Max Token Out</th>
                      <th data-column="generic">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {models.map((model, index) => (
                      <tr key={index}>
                        <td>{model.model_id}</td>
                        <td>{model.name}</td>
                        <td style={{textAlign: 'right'}}>${model.price_in?.toLocaleString()}</td>
                        <td style={{textAlign: 'right'}}>${model.price_out?.toLocaleString()}</td>
                        <td style={{textAlign: 'right'}}>{model.max_tokens_in?.toLocaleString()}</td>
                        <td style={{textAlign: 'right'}}>{model.max_tokens_out?.toLocaleString()}</td>
                        <td>{model?.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {/* <div className="edit-form-column-3">
          </div> */}
        </div>
      </div>
      {showCancelModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-compact">
            <h3>Unsaved Changes</h3>
            <p>You have unsaved changes. Are you sure you want to leave without saving?</p>
            <div className="modal-buttons">
              <button onClick={() => setShowCancelModal(false)} className="btn-cancel">Stay</button>
              <button onClick={() => { setShowCancelModal(false); onCancel(); }} className="btn-save">Leave</button>
            </div>
          </div>
        </div>
      )}
      {showSaveModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-compact">
            <h3>{saveSuccess ? 'Saved' : 'Unable to save'}</h3>
            <p>{saveMessage}</p>
            <div className="modal-buttons">
              <button onClick={() => setShowSaveModal(false)} className="btn-save">OK</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ModelconnectorEdit;
